import React, { useEffect, useRef, useState } from "react";
import { toast } from 'react-toastify';
import DataService from '../../services/data.service';
import LoaderOverlay from '../common/loader.component';

// Bootstrap-only version (no Tailwind)
export default function HeroBuilder({ initial, onChange }) {
  const defaultConfig = {
    header: "Your headline here",
    subheader: "A short supporting subheading goes here.",
    bgColor: "#f8f9fa",
    textColor: "#212529",
    imageDataUrl: undefined,
    imageFit: "cover",
    layout: "image-bottom",
    footerText: "Your small footer text goes here.",
    enabled: false,
  };

  const [cfg, setCfg] = useState({ ...defaultConfig, ...initial });
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // keep parent synced (optional)
  useEffect(() => {
    if (onChange) onChange(cfg);
  }, [cfg, onChange]);

  // Load from GET API on mount
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const res = await (DataService.getPromotion ? DataService.getPromotion() : Promise.resolve(null));
        const payload = res?.data ?? res;
        const data = payload?.data ?? payload;

        if (!ignore && data) {
          // snake_case -> camelCase (if backend returns snake_case)
          const mapped = {
            header: data.header ?? cfg.header,
            subheader: data.subheader ?? cfg.subheader,
            bgColor: data.bgColor ?? data.bg_color ?? cfg.bgColor,
            textColor: data.textColor ?? data.text_color ?? cfg.textColor,
            imageDataUrl: data.imageDataUrl ?? data.image_data ?? cfg.imageDataUrl,
            imageFit: data.imageFit ?? data.image_fit ?? cfg.imageFit,
            layout: data.layout ?? cfg.layout,
            footerText: data.footerText ?? data.footer_text ?? cfg.footerText,
            enabled: data.enabled ?? cfg.enabled,
            id : data.id ?? cfg.id,
          };
          setCfg(prev => ({ ...prev, ...mapped }));
        }
      } catch (e) {
        toast.error('Failed to load promotion.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => { ignore = true; };
  }, []);

  const handleFile = (file) => {
    if (!file) {
      setCfg((c) => ({ ...c, imageDataUrl: undefined }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result;
      setCfg((c) => ({ ...c, imageDataUrl: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    setSaving(true);
    try {
      await DataService.savePromotion(cfg);
      toast.success('Promotion saved successfully.');
    } catch (err) {
      toast.error('Failed to add promotion.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <LoaderOverlay loading={loading} />
    <div className="d-flex flex-column min-vh-100 bg-light p-3">
      <div className="container my-3">
        <div className="row g-3">
          {/* Controls */}
          <div className="col-12 col-lg-6">
            <section className="card shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h2 className="h5 fw-semibold m-0">Promotion Settings</h2>
                  {(loading || saving) && (
                    <span className="small text-muted">{loading ? 'Loading…' : 'Saving…'}</span>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label small text-secondary">Header</label>
                  <input
                    className="form-control"
                    value={cfg.header}
                    onChange={(e) => setCfg((c) => ({ ...c, header: e.target.value }))}
                    placeholder="Enter headline"
                    disabled={loading || saving}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small text-secondary">Subheader</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={cfg.subheader}
                    onChange={(e) => setCfg((c) => ({ ...c, subheader: e.target.value }))}
                    placeholder="Add a supporting line"
                    disabled={loading || saving}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small text-secondary">Footer Small Text</label>
                  <input
                    className="form-control"
                    value={cfg.footerText}
                    onChange={(e) => setCfg((c) => ({ ...c, footerText: e.target.value }))}
                    placeholder="Enter small text below button"
                    disabled={loading || saving}
                  />
                </div>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-secondary">Background Color</label>
                    <input
                      type="color"
                      className="form-control form-control-color w-100"
                      value={cfg.bgColor}
                      onChange={(e) => setCfg((c) => ({ ...c, bgColor: e.target.value }))}
                      disabled={loading || saving}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-secondary">Text Color</label>
                    <input
                      type="color"
                      className="form-control form-control-color w-100"
                      value={cfg.textColor}
                      onChange={(e) => setCfg((c) => ({ ...c, textColor: e.target.value }))}
                      disabled={loading || saving}
                    />
                  </div>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-secondary px-1">Layout</label>
                    <select
                      className="form-control form-select custom-select-style"
                      value={cfg.layout}
                      onChange={(e) => setCfg((c) => ({ ...c, layout: e.target.value }))}
                      disabled={loading || saving}
                    >
                      <option value="image-right">Image Right</option>
                      <option value="image-left">Image Left</option>
                      <option value="image-top">Image Top</option>
                      <option value="image-bottom">Image Bottom</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-secondary px-1">Image Fit</label>
                    <select
                      className="form-control form-select custom-select-style"
                      value={cfg.imageFit}
                      onChange={(e) => setCfg((c) => ({ ...c, imageFit: e.target.value }))}
                      disabled={loading || saving}
                    >
                      <option value="cover">Cover</option>
                      <option value="contain">Contain</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="form-label small text-secondary">Hero Image</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    disabled={loading || saving}
                  />
                </div>
                {cfg.imageDataUrl && (
    <div className="mt-3 text-center">
      <img
        src={cfg.imageDataUrl}
        alt="Preview"
        className="img-fluid rounded shadow-sm mb-2"
        style={{ maxHeight: "160px", objectFit: cfg.imageFit }}
      />
      <br />
      <button
        type="button"
        className="btn btn-outline-danger btn-sm"
        onClick={() => setCfg((c) => ({ ...c, imageDataUrl: undefined }))}
      >
        Remove Image
      </button>
    </div>
  )}

                <div className="mt-3 d-flex align-items-center gap-2">
                  <label className="form-label small text-secondary mb-0 px-2">Promotion</label>
                  <button
                    type="button"
                    className={`btn ${cfg.enabled ? "btn-success" : "btn-outline-secondary"} btn-sm`}
                    onClick={() => setCfg((c) => ({ ...c, enabled: !c.enabled }))}
                    aria-pressed={!!cfg.enabled}
                    disabled={loading || saving}
                  >
                    {cfg.enabled ? "Enabled" : "Disabled"}
                  </button>
                </div>

                <div className="mt-3">
                  <button type="submit" onClick={submit} className="btn btn-primary" disabled={saving || loading}>
                    {saving ? 'Saving…' : (loading ? 'Loading…' : 'Save Promotion')}
                  </button>
                </div>
              </div>
            </section>
          </div>

          {/* Live Preview */}
          <div className="col-12 col-lg-6">
            <section className="card shadow-sm h-100">
              <div className="card-body d-flex flex-column justify-content-between">
                <div>
                  <h2 className="h5 fw-semibold mb-3">Live Preview</h2>
                  <HeroPreview cfg={cfg} />
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <style>{`
        .btn.btn-primary {
          background: #ff48b0 !important;
          border: 1px solid #ff48b0 !important;
          color: #fff !important;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 0.05em;
          transition: all 0.3s ease;
        }
        .btn.btn-primary:hover {
          background: #ff2b9c !important;
          border-color: #ff2b9c !important;
        }
      `}</style>
    </div>
    </>
  );
}

function HeroPreview({ cfg }) {
  const textBlock = (
    <div>
      <h2 className="display-6 fw-bold mb-2" style={{ color: cfg.textColor }}>
        {cfg.header}
      </h2>
      <p className="lead" style={{ color: cfg.textColor, fontSize: '14px' }}>
        {cfg.subheader}
      </p>
    </div>
  );

  const imageBlock = cfg.imageDataUrl ? (
    <img
      src={cfg.imageDataUrl}
      alt="Hero"
      className="img-fluid rounded-3"
      style={{ objectFit: cfg.imageFit, width: "100%" }}
    />
  ) : (
    <div className="border rounded-3 p-4 text-center text-secondary" style={{ opacity: 0.6 }}>
      <img src="images/prom.webp" alt="Divine Blush Beauty Logo" className="img-fluid" />
    </div>
  );

  let content;
  switch (cfg.layout) {
    case "image-left":
      content = (
        <div className="row g-3 align-items-center">
          <div className="col-md-6">{imageBlock}</div>
          <div className="col-md-6">{textBlock}</div>
        </div>
      );
      break;
    case "image-top":
      content = (
        <div className="d-flex flex-column gap-3">
          {imageBlock}
          {textBlock}
        </div>
      );
      break;
    case "image-bottom":
      content = (
        <div className="d-flex flex-column gap-3">
          {textBlock}
          {imageBlock}
        </div>
      );
      break;
    default:
      content = (
        <div className="row g-3 align-items-center">
          <div className="col-md-6">{textBlock}</div>
          <div className="col-md-6">{imageBlock}</div>
        </div>
      );
  }

  return (
    <div
      className="rounded-3 p-4 border d-flex flex-column justify-content-between text-center"
      style={{ background: cfg.bgColor, minHeight: 420 }}
    >
      {/* Logo & brand inside preview */}
      <div className="d-flex align-items-center gap-2 mb-3 justify-content-center">
        <img
          src="images/Logo_salon.svg"
          alt="Divine Blush Beauty Logo"
          className="img-fluid"
          style={{ height: 40, width: 40, objectFit: "contain" }}
        />
        <span
          className="text-uppercase fw-semibold"
          style={{ color: "#ff48b0", letterSpacing: ".06em" }}
        >
          Divine Blush Beauty
        </span>
      </div>

      {/* Main hero content */}
      <div className="flex-grow-1">{content}</div>

      {/* Button and footer inside background */}
      <div className="mt-4">
        <button className="btn btn-primary px-4 py-3 fw-semibold text-uppercase shadow">
          MAKE AN APPOINTMENT
        </button>
        {cfg.footerText && (
          <p className="small mt-3 mb-0 text-secondary" style={{ lineHeight: 1.6 }}>
            {cfg.footerText}
          </p>
        )}
      </div>
    </div>
  );
}
